import React, { useState, useEffect, useCallback } from 'react';
import { Smartphone } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/axios';
import CFiltersBar from '../components/shared/c-filters-bar';
import CTable from '../components/shared/c-table';
import StatusBadge from '../components/StatusBadge';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateUtils';
import usePersistedState from '../hooks/usePersistedState';

const DEVICE_STATUSES = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'signed_out', label: 'Signed out' },
];

const DECISION_LABEL = { approved: 'Approve', rejected: 'Reject', revoked: 'Revoke' };

/**
 * HR queue for phones registered by the mobile timesheet app. A device must be
 * approved before it can punch; one approved device per user, so approving one
 * revokes the others server side.
 */
const MobileDevices = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('devices', 'manage');
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = usePersistedState('devices.status', 'pending');
  // The decision being confirmed inline: { id, status } plus its optional note.
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState('');
  const [acting, setActing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/mobile/devices', {
        params: statusFilter ? { status: statusFilter } : {},
      });
      setDevices(res.data.data?.devices || []);
    } catch {
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startDecision = (id, status) => {
    setDecision({ id, status });
    setNote('');
  };

  const confirmDecision = async () => {
    setActing(true);
    try {
      await api.put(`/api/mobile/devices/${decision.id}`, {
        status: decision.status,
        note: note.trim() || null,
      });
      toast.success(`Device ${decision.status}`);
      setDecision(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update device');
    } finally {
      setActing(false);
    }
  };

  const actionButton = (device, status, className) => (
    <button
      type="button"
      onClick={() => startDecision(device.id, status)}
      data-testid={`device-${device.id}-${status}`}
      className={`rounded border px-2 py-1 text-xs font-medium ${className}`}
    >
      {DECISION_LABEL[status]}
    </button>
  );

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      cellClassName: 'font-medium',
      render: (device) => (device.first_name ? `${device.first_name} ${device.last_name}` : device.username),
    },
    { key: 'email', header: 'Email', cellClassName: 'text-gray-500' },
    {
      key: 'device',
      header: 'Device',
      render: (device) => (
        <>
          {device.platform} {device.model || device.name}
          <span className="block text-xs text-gray-400">app {device.app_version || '-'}</span>
        </>
      ),
    },
    {
      key: 'registered',
      header: 'Registered',
      cellClassName: 'text-gray-500',
      render: (device) => formatDateTime(device.created_at),
    },
    {
      key: 'last_seen',
      header: 'Last seen',
      cellClassName: 'text-gray-500',
      render: (device) => formatDateTime(device.last_seen_at),
    },
    {
      key: 'status',
      header: 'Status',
      render: (device) => (
        <StatusBadge status={device.status} type="leave" data-testid={`device-${device.id}-status`} />
      ),
    },
    ...(canManage ? [{
      key: 'actions',
      header: '',
      align: 'right',
      cellClassName: 'whitespace-normal',
      render: (device) => (decision?.id === device.id ? (
        <div className="inline-block w-64 text-left" data-testid={`device-${device.id}-confirm`}>
          <p className="text-xs text-gray-600">
            {DECISION_LABEL[decision.status]} this device?
          </p>
          <input
            type="text"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Note (optional)"
            aria-label="Decision note"
            className="mt-1 block w-full rounded-md border border-gray-300 p-1.5 text-xs"
          />
          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setDecision(null)}
              className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDecision}
              disabled={acting}
              data-testid={`device-${device.id}-confirm-yes`}
              className={'rounded bg-primary-600 px-2 py-1 text-xs font-medium text-white'
                + ' hover:bg-primary-700 disabled:opacity-60'}
            >
              {acting ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-2">
          {device.status !== 'approved'
            && actionButton(device, 'approved', 'border-success-600 text-success-700 hover:bg-success-50')}
          {device.status === 'pending'
            && actionButton(device, 'rejected', 'border-danger-600 text-danger-700 hover:bg-danger-50')}
          {device.status === 'approved'
            && actionButton(device, 'revoked', 'border-gray-300 text-gray-700 hover:bg-gray-50')}
        </div>
      )),
    }] : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Smartphone className="h-7 w-7 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mobile devices</h1>
          <p className="text-gray-600">Phones registered by the timesheet app. Only an approved phone can punch.</p>
        </div>
      </div>

      <CFiltersBar
        showSearch={false}
        clearTestId="devices-clear-filters"
        filters={[
          {
            key: 'status',
            testId: 'devices-status-filter',
            label: 'Status',
            value: statusFilter,
            onChange: (event) => setStatusFilter(event.target.value),
            options: DEVICE_STATUSES,
          },
        ]}
        onClear={() => setStatusFilter('')}
      />

      <CTable
        columns={columns}
        data={devices}
        loading={loading}
        rowKey="id"
        emptyMessage={
          <div className="text-gray-400">
            <Smartphone className="mx-auto h-12 w-12 mb-3" />
            <p className="text-sm">
              {statusFilter === 'pending' ? 'No devices waiting for approval' : 'No devices found'}
            </p>
          </div>
        }
        getRowProps={(device) => ({ 'data-testid': `device-row-${device.id}` })}
      />
    </div>
  );
};

export default MobileDevices;
